import { makeObservable, observable, action } from "mobx";
import service from '@/services/axios';
import UserInfoData from "@/base/UserInfoData";
import UserAssetData from "@/base/UserAssetData";
import UserManagementAPI from '@/api/userManagement';

// mobX好像不能跟踪对象的属性，只能跟踪字段，所以我们要把
// UserInfoData和UserAssetData的字段flat到UserInfoStore中

class UserInfoStore {
    userInfoData: UserInfoData;
    userAssetData: UserAssetData;

    constructor() {
        this.userInfoData = new UserInfoData();
        this.userAssetData = new UserAssetData();

        makeObservable(this, {
            userInfoData: observable,
            userAssetData: observable,
            setUserInfoData: action,
            setUserAssertData: action,
            updateUserInfoFromData: action,
            updateUserAssetsFromData: action,
            refreshUserInfo: action,
            refreshUserAssets: action,
            updateUserAssets: action,
            consumeDiamonds: action,
            resetUserInfo: action,
        });
    }

    setUserInfoData({
        id,
        userRole,
        userView,
        name,
        realUsername,
        phone,
        wechatID,
        avatarUrl,
        tagLine,
        age,
        gender
    }: Partial<UserInfoData> = {}) {
        if (id) {
            this.userInfoData.id = id;
        }
        if (userRole) {
            this.userInfoData.userRole = userRole;
        }
        if (userView) {
            this.userInfoData.userView = userView;
        } else {
            if (userRole === 1) {
                this.userInfoData.userView = localStorage.getItem('userView') || 'student';
            } else {
                this.userInfoData.userView = 'student'
            }
        }
        if (name) {
            this.userInfoData.name = name;
        }
        if (realUsername) {
            this.userInfoData.realUsername = realUsername;
        }
        if (phone) {
            this.userInfoData.phone = phone;
        }
        if (wechatID) {
            this.userInfoData.wechatID = wechatID;
        }
        if (avatarUrl) {
            this.userInfoData.avatarUrl = avatarUrl;
        }
        if (tagLine) {
            this.userInfoData.tagLine = tagLine;
        }
        if (age) {
            this.userInfoData.age = age;
        }
        if (gender) {
            this.userInfoData.gender = gender;
        }
    }

    setUserAssertData({
        id,
        numCoins,
        numDiamonds,
        medals
    }: Partial<UserAssetData> = {}) {
        if (id !== undefined) {
            this.userAssetData.id = id;
        }
        if (numCoins !== undefined) {
            this.userAssetData.numCoins = numCoins;
        }
        if (numDiamonds !== undefined) {
            this.userAssetData.numDiamonds = numDiamonds;
        }
        if (medals !== undefined) {
            this.userAssetData.medals = medals;
        }
    }



    // 使用 setUserInfoData 来更新用户信息
    updateUserInfoFromData = (data: any) => {
        this.setUserInfoData({
            id: data.id,
            userRole: data.role,
            name: data.name,
            realUsername: data.real_username,
            phone: data.phone,
            avatarUrl: data.avatar_url,
            tagLine: data?.info?.tagline,
            gender: data?.info?.gender,
            age: data?.info?.age,
        });
    }

    updateUserAssetsFromData = (data: any) => {
        this.setUserAssertData({
            id: data.user_id,
            // 需要做转换，和数据库里的schema不一样
            numCoins: data.points,
            numDiamonds: data.stars,
            medals: data.extras.medals || [],
        });
    }

    refreshUserInfo = async (userId: number = 0): Promise<void> => {
        try {
            let response: any;
            if (userId === 0) {
                response = await service.get('/me');
                if (response.status === 'success') {
                    userId = response.data.id;
                }
            }
            if (userId) {
                response = await service.get(`/user/${userId}`);
                if (response.status === 'success') {
                    this.updateUserInfoFromData(response.data);
                }
            }
        } catch (error) {
            console.error(error);
        }
    }

    refreshUserAssets = async (userId: number): Promise<void> => {
        try {
            console.log('refresh user assets');
            const userAPI = new UserManagementAPI();
            const response = await userAPI.getUserAssets(userId);
            console.log(response.data);
            if (response.status === 'success') {
                this.updateUserAssetsFromData(response.data);
            }
        } catch (error) {
            console.error(error);
        }
    }

    updateUserAssets = async (data: any): Promise<void> => {
        try {
            const userManagementAPI = new UserManagementAPI();
            const res = await userManagementAPI.UpdateUserAssetsExpress(data);
            if (res.status === 'success') {
                this.updateUserAssetsFromData(res.data);
            } else {
                throw new Error('Failed to refresh user assets');
            }
        } catch (error) {
            console.error('Error reward user', error);
            throw error;
        }
    }

    // 游乐场消耗钻石，根据不同的消耗类型，消耗不同数量的钻石，返回成功与否
    consumeDiamonds = async (consumeType: string): Promise<{ status: string, message: string }> => {
        try {
            // 根据消费类型确定需要扣除的钻石数量  
            let diamondsToConsume = 0;
            switch (consumeType) {
                case 'music_gen': // 音乐生成 
                    diamondsToConsume = 10;
                    break;
                case 'chat': // AI聊天  
                    diamondsToConsume = 1;
                    break;
                case 'drawing': // AI作画  
                    diamondsToConsume = 20;
                    break;
                case 'video_gen': // 视频生成  
                    diamondsToConsume = 20;
                    break;
                default:
                    return { status: 'error', message: 'Invalid consume type' }; // 消费类型无效  
            }

            // 检查当前用户的钻石是否够用  
            if (this.userAssetData.numDiamonds < diamondsToConsume) {
                return { status: 'error', message: 'Not enough diamonds' }; // 钻石不足提示  
            }

            // 准备请求数据，用于API调用  
            const data: any = {
                user_id: this.userInfoData.id,
                stars_to_change: -diamondsToConsume // 负数表示扣除钻石 
            };

            // 调用接口尝试扣除指定数量的钻石  
            const userManagementAPI = new UserManagementAPI();
            const res = await userManagementAPI.UpdateUserAssetsExpress(data);

            if (res.status === 'success') {
                // 扣除成功后，更新用户资产信息  
                this.updateUserAssetsFromData(res.data);
                return { status: 'success', message: 'Diamonds deducted successfully' };
            } else {
                // 如果扣除失败，提示失败原因  
                return { status: 'error', message: res.message || 'Failed to deduct diamonds. Please try again.' };
            }
        } catch (error) {
            console.error('An error occurred while processing diamond consumption:', error);
            return { status: 'error', message: 'An unexpected error occurred. Please try again later.' }; // 返回错误提示  
        }
    };

    resetUserInfo() {
        this.userInfoData.userRole = 0;
        this.userInfoData.name = '';
        this.userInfoData.realUsername = '';
        this.userInfoData.id = 0;
        this.userInfoData.avatarUrl = '';
        this.userAssetData = new UserAssetData();
    }

    isTeacherView() {
        return this.userInfoData.userRole === 1 && this.userInfoData.userView === "teacher";
    }
}

const userInfoStore = new UserInfoStore();
export { UserInfoStore, userInfoStore };
